/**
 * PushEnableButton — Iter267
 *
 * Compact bell-with-plus button that lets an operator opt-in to Web Push
 * alerts. Hides itself when the browser doesn't support push OR the user
 * already has an active subscription (in that case the standard
 * SmithNotificationBell handles the visible bell UI).
 *
 * Props:
 *   • variant — "chip" (default, header-safe) | "primary"
 */
import { useState } from "react";
import { BellPlus, BellOff, Loader2, CheckCircle2, AlertTriangle, X } from "lucide-react";
import usePushSubscription from "../hooks/usePushSubscription";

export default function PushEnableButton({ variant = "chip" }) {
  const push = usePushSubscription();
  const [modalOpen, setModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!push.supported) return null;
  if (push.status === "subscribed") return null;      // already opted-in
  if (push.status === "denied") return null;          // user hard-denied

  const isChip = variant === "chip";
  const cls = isChip
    ? "h-8 px-2.5 rounded-lg text-[11px]"
    : "h-9 sm:h-10 px-3.5 rounded-xl text-[12.5px]";

  const handleEnable = async () => {
    setBusy(true);
    const sub = await push.subscribe();
    setBusy(false);
    if (sub) {
      // Fire a smoke push so the operator instantly sees it work.
      await push.testPush();
    }
    setModalOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        data-testid="push-enable-btn"
        className={`inline-flex items-center gap-1.5 font-black tracking-tight border border-amber-400/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/25 hover:border-amber-400/70 transition ${cls}`}
        title="Ativar alertas críticos por push"
      >
        <BellPlus className={isChip ? "w-3 h-3" : "w-3.5 h-3.5"} />
        {isChip ? "Alertas" : "Ativar alertas"}
      </button>

      {modalOpen && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4"
             data-testid="push-enable-overlay"
             style={{ background: "rgba(2,6,23,0.78)", backdropFilter: "blur(6px)" }}
             onClick={() => setModalOpen(false)}>
          <div className="w-full max-w-md rounded-2xl border shadow-2xl mobile-sheet overflow-hidden"
               style={{ background: "#0B1120", borderColor: "rgba(245,158,11,0.35)" }}
               onClick={(e) => e.stopPropagation()}>
            <div className="px-5 pt-5 pb-3 flex items-start justify-between">
              <div>
                <div className="text-[10.5px] font-black uppercase tracking-widest text-amber-300">
                  Push notifications
                </div>
                <h3 className="text-[18px] font-black text-white mt-1">
                  Alertas críticos direto no seu iPhone
                </h3>
              </div>
              <button onClick={() => setModalOpen(false)}
                      data-testid="push-enable-close"
                      className="w-10 h-10 -mr-2 rounded-full hover:bg-white/10 text-slate-400 flex items-center justify-center touch-target">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 pb-5 space-y-3">
              <p className="text-[13px] text-slate-300 leading-relaxed">
                Receba um <b className="text-amber-300">alerta imediato</b> no lock screen
                sempre que um <b className="text-amber-300">foreclosure crítico</b> for
                detectado num prospect — sem precisar deixar o app aberto.
              </p>

              <ul className="space-y-2 text-[12.5px] text-slate-300">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  Só sinais <b className="text-white">CRITICAL</b> (foreclosure agendado) — sem spam
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  Tocando no alerta abre direto o card do prospect
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  Você pode desativar a qualquer momento em Ajustes
                </li>
              </ul>

              {push.status === "error" && (
                <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-[11.5px] text-red-200 inline-flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Falha ao ativar. Verifique se as notificações estão permitidas no navegador.</span>
                </div>
              )}

              <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
                <button type="button" onClick={() => setModalOpen(false)}
                        data-testid="push-enable-later"
                        className="h-11 sm:h-9 px-4 rounded-xl text-[12.5px] font-bold text-slate-300 hover:bg-white/10">
                  Mais tarde
                </button>
                <button type="button" onClick={handleEnable}
                        disabled={busy}
                        data-testid="push-enable-confirm"
                        className="h-11 sm:h-9 px-4 rounded-xl text-[12.5px] font-black text-[#0A1233] inline-flex items-center justify-center gap-1.5 disabled:opacity-60 flex-1"
                        style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)" }}>
                  {busy
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <BellPlus className="w-3.5 h-3.5" />}
                  Ativar alertas
                </button>
              </div>
              <p className="text-[10.5px] text-slate-500 text-center">
                {"iOS 16.4+ · Android · Chrome/Edge/Firefox desktop"}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Iter267 · Simple "off" indicator for the Settings panel — always visible
// there. Not used in the standard header.
export function PushDisableButton() {
  const push = usePushSubscription();
  if (!push.supported || push.status !== "subscribed") return null;
  return (
    <button type="button" onClick={push.unsubscribe}
            data-testid="push-disable-btn"
            className="h-8 px-2.5 rounded-lg text-[11px] font-bold border border-white/15 text-slate-300 hover:border-red-400/60 hover:text-red-200 inline-flex items-center gap-1.5">
      <BellOff className="w-3 h-3" />
      Desativar alertas
    </button>
  );
}
